import { useId, type FormEvent } from 'react'

import { COMPETITOR_FREQUENCIES, COMPETITOR_FREQUENCY_LABELS, type CompetitorFrequency } from '@/api/market'

import { useAddCompetitor, useFormFields } from './hooks'

const EMPTY = { name: '', domain: '', brand: '', frequency: 'monthly' as CompetitorFrequency, typicalTicketPrice: '', prizeCategories: '' }

/** A manual-entry form for one competitor, per the acceptance criteria's Competitor model. */
export function AddCompetitorForm({ onDone }: { onDone: () => void }) {
  const formId = useId()
  const { values, update, reset } = useFormFields(EMPTY)
  const addCompetitor = useAddCompetitor()

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    try {
      await addCompetitor.mutateAsync({
        name: values.name.trim(),
        domain: values.domain.trim(),
        brand: values.brand.trim(),
        frequency: values.frequency,
        typicalTicketPrice: Number(values.typicalTicketPrice) || 0,
        prizeCategories: values.prizeCategories
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean),
      })
      reset()
      onDone()
    } catch {
      // addCompetitor.isError / .error drive the message below; the form stays open so the viewer can retry.
    }
  }

  return (
    <form className="mk-form" aria-label="Add a competitor" onSubmit={(event) => void handleSubmit(event)}>
      <div className="mk-form__grid">
        <label htmlFor={`${formId}-name`}>
          Name
          <input id={`${formId}-name`} required value={values.name} onChange={(event) => update('name', event.target.value)} />
        </label>
        <label htmlFor={`${formId}-domain`}>
          Domain
          <input
            id={`${formId}-domain`}
            required
            placeholder="example.com"
            value={values.domain}
            onChange={(event) => update('domain', event.target.value)}
          />
        </label>
        <label htmlFor={`${formId}-brand`}>
          Brand
          <input id={`${formId}-brand`} required value={values.brand} onChange={(event) => update('brand', event.target.value)} />
        </label>
        <label htmlFor={`${formId}-frequency`}>
          Frequency
          <select id={`${formId}-frequency`} value={values.frequency} onChange={(event) => update('frequency', event.target.value as CompetitorFrequency)}>
            {COMPETITOR_FREQUENCIES.map((frequency) => (
              <option key={frequency} value={frequency}>
                {COMPETITOR_FREQUENCY_LABELS[frequency]}
              </option>
            ))}
          </select>
        </label>
        <label htmlFor={`${formId}-price`}>
          Typical ticket price
          <input
            id={`${formId}-price`}
            type="number"
            min="0"
            step="0.01"
            required
            value={values.typicalTicketPrice}
            onChange={(event) => update('typicalTicketPrice', event.target.value)}
          />
        </label>
        <label htmlFor={`${formId}-categories`}>
          Prize categories
          <input
            id={`${formId}-categories`}
            placeholder="Tech, Cars"
            value={values.prizeCategories}
            onChange={(event) => update('prizeCategories', event.target.value)}
          />
        </label>
        {addCompetitor.isError ? (
          <p className="mk-form__failure" role="alert">
            Could not add this competitor. Try again.
          </p>
        ) : null}
      </div>
      <div className="mk-form__actions">
        <button type="submit" className="ci-button" data-variant="primary" disabled={addCompetitor.isPending}>
          Add competitor
        </button>
        <button type="button" className="ci-button" data-variant="ghost" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  )
}
